import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, TextEditor } from '@approvalmax/ui';
import { stringHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useDispatch, useSelector } from 'modules/react-redux';
import { XeroContact } from 'pages/requestForm/data/xero/XeroContact';
import {
    getCanCreateContact,
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
    RequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { getXeroContext } from 'pages/requestForm/selectors/xero';
import { FC, memo, useCallback, useMemo, useRef } from 'react';

import { changeXeroSupplier, editRequestField, showXeroContactCreationPopup } from '../../../../../actions';
import { getShowValidationErrors } from '../../../../../selectors/pageSelectors';
import CustomerContactDataProvider from '../../../dataProviders/CustomerContactDataProvider';
import { XeroContactDetails } from '../../../XeroContactDetails/XeroContactDetails';
import { messages } from './XeroQuoteFieldsSection.messages';
import { ContactFieldItem, FieldItem, Root } from './XeroQuoteFieldsSection.styles';

const { xeroConstants, commonConstants } = constants;

type XeroQuoteFieldsSectionProps = {
    request: domain.XeroQuoteRequest;
};

const XeroQuoteFieldsSection: FC<XeroQuoteFieldsSectionProps> = memo((props) => {
    const { request } = props;
    const dispatch = useDispatch();
    const xeroContext = useSelector(getXeroContext);

    const { contact, date, expiryDate, number, reference, brandingTheme } = request.details;
    const showValidationErrors = useSelector(getShowValidationErrors);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;
    const initialBrandingTheme = useRef(brandingTheme);

    const [
        expiryFieldPermission,
        numberFieldPermission,
        referenceFieldPermission,
        brandingFieldPermission,
        dateFieldPermission,
        customerFieldPermission,
    ] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [
            domain.FieldSystemPurpose.XeroQuoteExpiry,
            domain.FieldSystemPurpose.XeroQuoteNumber,
            domain.FieldSystemPurpose.XeroReference,
            domain.FieldSystemPurpose.XeroBranding,
            domain.FieldSystemPurpose.XeroDate,
            domain.FieldSystemPurpose.XeroCustomer,
        ])
    );

    const createContact = useCallback(
        (text: string | null) => {
            dispatch(showXeroContactCreationPopup(text));

            return Promise.resolve(null);
        },
        [dispatch]
    );

    const onChangeSupplier = useCallback(
        (supplier: XeroContact | null) => {
            dispatch(changeXeroSupplier(supplier));
        },
        [dispatch]
    );

    const onChangeDate = useCallback(
        (date: string | null) => {
            dispatch(editRequestField<domain.XeroQuoteDetails>('date', date));
        },
        [dispatch]
    );

    const onChangeExpiryDate = useCallback(
        (date: string | null) => {
            dispatch(editRequestField<domain.XeroQuoteDetails>('expiryDate', date));
        },
        [dispatch]
    );

    const onChangeNumber = useCallback(
        (number: string) => {
            dispatch(editRequestField<domain.XeroQuoteDetails>('number', number));
        },
        [dispatch]
    );

    const onChangeReference = useCallback(
        (reference: string) => {
            if (commonConstants.ONLY_SPACES_REG_EXP.test(reference)) {
                return;
            }

            dispatch(
                editRequestField<domain.XeroPurchaseOrderDetails>(
                    'reference',
                    stringHelpers.escapeSpecificExpressions(reference, xeroConstants.XERO_NOT_ALLOWED_SYMBOLS)
                )
            );
        },
        [dispatch]
    );

    const onChangeBrandingTheme = useCallback(
        (brandingTheme: Reference | null) => {
            dispatch(editRequestField<domain.XeroQuoteDetails>('brandingTheme', brandingTheme));
        },
        [dispatch]
    );

    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));

    const createItemDisplayTextFn = useCallback(() => messages.addCustomerButton, []);

    const isQuoteNumberInvalid =
        showValidationErrors &&
        (number?.length > xeroConstants.QUOTE_NUMBER_MAX_LENGTH || (numberFieldPermission.required && !number));
    const isReferenceInvalid =
        showValidationErrors &&
        ((reference?.length ?? 0) > xeroConstants.PO_REFERENCE_MAX_LENGTH ||
            (referenceFieldPermission.required && !reference));

    const brandingThemeOptions = useMemo(
        () =>
            isApproverMode && initialBrandingTheme.current
                ? [initialBrandingTheme.current, ...xeroContext.brandingThemes]
                : xeroContext.brandingThemes,
        [isApproverMode, xeroContext.brandingThemes]
    );

    return (
        <Root>
            <ContactFieldItem required title={messages.customerFieldTitle}>
                <CustomerContactDataProvider>
                    <DropdownEditor
                        invalid={showValidationErrors && !contact}
                        value={contact}
                        singleLinePerItem={false}
                        onChange={onChangeSupplier}
                        placeholder={messages.customerFieldPlaceholder}
                        canCreateItemFn={() => canCreateContact}
                        createItemDisplayTextFn={createItemDisplayTextFn}
                        onCreateItem={createContact}
                        disabled={customerFieldPermission.disabled}
                    />
                </CustomerContactDataProvider>

                <XeroContactDetails contact={contact} />
            </ContactFieldItem>

            <FieldItem required title={messages.dateFieldTitle}>
                <DateTimeEditor
                    invalid={showValidationErrors && !date}
                    value={date}
                    onChange={onChangeDate}
                    placeholder={messages.dateFieldPlaceholder}
                    disabled={dateFieldPermission.disabled}
                />
            </FieldItem>

            <FieldItem title={messages.expiryDateFieldTitle} required={expiryFieldPermission.required}>
                <DateTimeEditor
                    value={expiryDate}
                    onChange={onChangeExpiryDate}
                    placeholder={messages.expiryDateFieldPlaceholder}
                    disabled={expiryFieldPermission.disabled}
                    invalid={showValidationErrors && expiryFieldPermission.required && !expiryDate}
                />
            </FieldItem>

            <FieldItem title={messages.quoteNumberFieldTitle} required={numberFieldPermission.required}>
                <TextEditor
                    invalid={isQuoteNumberInvalid}
                    value={number}
                    maxLength={xeroConstants.QUOTE_NUMBER_MAX_LENGTH}
                    onChange={onChangeNumber}
                    placeholder={messages.quoteNumberFieldPlaceholder}
                    disabled={numberFieldPermission.disabled}
                />
            </FieldItem>

            <FieldItem title={messages.referenceFieldTitle} required={referenceFieldPermission.required}>
                <TextEditor
                    invalid={isReferenceInvalid}
                    value={reference ?? ''}
                    maxLength={xeroConstants.PO_REFERENCE_MAX_LENGTH}
                    onChange={onChangeReference}
                    placeholder={messages.referenceFieldPlaceholder}
                    disabled={referenceFieldPermission.disabled}
                />
            </FieldItem>

            <FieldItem title={messages.themeFieldTitle}>
                <PlainDataProvider items={brandingThemeOptions}>
                    <DropdownEditor
                        invalid={showValidationErrors && !brandingTheme}
                        value={brandingTheme}
                        onChange={onChangeBrandingTheme}
                        placeholder={messages.themeFieldPlaceholder}
                        buttonComponent={DropdownEditor.NoFilterButton}
                        disabled={brandingFieldPermission.disabled}
                    />
                </PlainDataProvider>
            </FieldItem>
        </Root>
    );
});

export default XeroQuoteFieldsSection;
